import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { apiClient, normalizeApiError } from '../../lib/apiClient'
import type { ApiResponse } from '../../types'
import Panel from '../../components/cards/Panel'
import PageHeader from '../../components/common/PageHeader'
import ErrorState from '../../components/common/ErrorState'
import EmptyState from '../../components/common/EmptyState'
import { Skeleton } from '../../components/common/Loading'
export interface Payment {
  id: number
  appointment_id: number
  transaction_ref: string
  amount: string
  currency: string
  status: string
  patient_name: string
  appointment_no: string
  receipt: { id: number; receipt_no: string } | null
}
export function PaymentsPage({ role }: { role: 'patient' | 'admin' }) {
  const [page, setPage] = useState(1)
  const q = useQuery({
    queryKey: [role, 'payments', page],
    queryFn: async () =>
      (
        await apiClient.get<ApiResponse<Payment[]>>('/' + role + '/payments', {
          params: { page },
        })
      ).data,
  })
  const refund = useMutation({
    mutationFn: async (p: Payment) => {
      const amount = window.prompt('Demo refund amount')
      const reason = window.prompt('Reason')
      if (!amount || !reason) return
      if (window.confirm('Confirm this demo refund?'))
        await apiClient.post('/admin/payments/' + p.id + '/refund', {
          amount,
          reason,
        })
    },
    onSuccess: () => q.refetch(),
  })
  return (
    <div className="page-container">
      <PageHeader
        title="Payments"
        description="Demo transactions and receipts"
      />
      <Panel title="Payment history">
        {refund.isError && (
          <ErrorState message={normalizeApiError(refund.error).message} />
        )}
        {q.isLoading ? (
          <Skeleton lines={5} />
        ) : q.isError ? (
          <ErrorState onRetry={() => q.refetch()} />
        ) : !q.data?.data.length ? (
          <EmptyState title="No payments" />
        ) : (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Appointment</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {q.data.data.map((p) => (
                  <tr key={p.id}>
                    <td>
                      {p.appointment_no}
                      <small className="d-block">{p.patient_name}</small>
                    </td>
                    <td>
                      {p.currency} {p.amount}
                    </td>
                    <td>{p.status}</td>
                    <td>
                      {role === 'patient' && p.status === 'pending' && (
                        <Link
                          className="btn btn-sm btn-primary"
                          to={'/patient/payments/' + p.id + '/demo'}
                        >
                          Demo checkout
                        </Link>
                      )}{' '}
                      {p.receipt && (
                        <Link
                          className="btn btn-sm btn-outline-primary"
                          to={'/' + role + '/receipts/' + p.receipt.id}
                        >
                          Receipt
                        </Link>
                      )}
                      {role === 'admin' &&
                        ['paid', 'partially_refunded'].includes(p.status) && (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            disabled={refund.isPending}
                            onClick={() => refund.mutate(p)}
                          >
                            Refund
                          </button>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="form-actions">
          <button
            className="btn btn-outline-secondary"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </button>
          <span>Page {page}</span>
          <button
            className="btn btn-outline-secondary"
            disabled={!q.data?.meta || page >= q.data.meta.last_page}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
        </div>
      </Panel>
    </div>
  )
}
export function DemoPaymentPage() {
  const { paymentId = '' } = useParams()
  const q = useQuery({
    queryKey: ['payment', paymentId],
    queryFn: async () =>
      (
        await apiClient.get<ApiResponse<Payment>>(
          '/patient/payments/' + paymentId,
        )
      ).data.data,
  })
  const m = useMutation({
    mutationFn: async (outcome: string) =>
      apiClient.post('/patient/payments/' + paymentId + '/simulate', {
        outcome,
      }),
    onSuccess: () => q.refetch(),
  })
  return (
    <div className="page-container">
      <PageHeader title="Demo checkout" />
      <Panel title="Demo payment only. No real money will be charged.">
        {q.isLoading ? (
          <Skeleton />
        ) : q.isError ? (
          <ErrorState />
        ) : (
          <>
            <p>
              {q.data?.appointment_no} — {q.data?.currency} {q.data?.amount}
            </p>
            <p role="status">Status: {q.data?.status}</p>
            {q.data?.status === 'pending' && (
              <div className="form-actions">
                {(['paid', 'failed', 'cancelled'] as const).map((s, i) => (
                  <button
                    className="btn btn-primary"
                    disabled={m.isPending}
                    key={s}
                    onClick={() => m.mutate(s)}
                  >
                    {
                      [
                        'Simulate Successful Payment',
                        'Simulate Failed Payment',
                        'Simulate Cancelled Payment',
                      ][i]
                    }
                  </button>
                ))}
              </div>
            )}
            {q.data?.receipt && (
              <Link to={'/patient/receipts/' + q.data.receipt.id}>
                View receipt
              </Link>
            )}
          </>
        )}
        {m.isError && (
          <ErrorState message={normalizeApiError(m.error).message} />
        )}
        <Link to="/patient/payments">Payment history</Link>
      </Panel>
    </div>
  )
}
export function ReceiptPage({ role }: { role: 'patient' | 'admin' }) {
  const { receiptId = '' } = useParams()
  const url = apiClient.getUri({
    url: '/' + role + '/receipts/' + receiptId + '/pdf',
  })
  return (
    <div className="page-container">
      <PageHeader title="Payment receipt" />
      <a
        href={url}
        className="btn btn-primary"
        target="_blank"
        rel="noreferrer"
      >
        Open / download PDF
      </a>
      <iframe
        title="A4 payment receipt"
        src={url}
        style={{ width: '100%', height: '80vh', border: 0, marginTop: 16 }}
      />
    </div>
  )
}
